import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMap, Marker, Popup } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import { Button } from "../../BaseComponents";
import { invisibleMarker, lock, delete_single_polygon } from "../../../assets/images";
import { getMidPoint } from "../DrawToolProvider/_draw.helpers";
import { canopyActions } from "../..";
import { Popconfirm } from "antd";
import { debounce } from "lodash";

const MapboxImageLayerContainer = () => {
  const images = useSelector((state) => state.canopy.images);
  if (!images) return null;
  return <>{Object.values(images).map((image) => image && <MapboxImageLayer key={image.imageId} imageData={image} />)}</>;
};

const MapboxImageLayer = ({ imageData }) => {
  const dispatch = useDispatch();
  const { current: mapRef } = useMap();
  const map = mapRef.getMap();

  const draggingRef = useRef(false);
  const currentMarkerRef = useRef(null);
  const startCoordRef = useRef(null);
  const updatedCoordinatesRef = useRef([]);
  const markersRef = useRef([]);
  const editMode = useRef(true);
  const animationFrameRef = useRef(null);
  const eventHandlersRef = useRef({});
  const isInitializedRef = useRef(false);
  const opacityRef = useRef(imageData.opacity || 0.9);
  const is_drawing = useSelector((state) => state.inputs.uiState.is_drawing);

  const [opacity, setOpacity] = useState(imageData.opacity || 0.9);
  const [popupVisible, setPopupVisible] = useState(true);
  const [popupPosition, setPopupPosition] = useState();
  const [selected_image, set_selected_image] = useState(undefined);

  // Sync opacity ref when opacity state changes
  useEffect(() => {
    opacityRef.current = opacity;
  }, [opacity]);

  // Define debounced functions before they're used
  const debouncedOpacityUpdate = useCallback(
    debounce((newOpacity) => {
      dispatch(canopyActions.updateImportedImages("edit_image", { opacity: newOpacity }, imageData.imageId));
    }, 500),
    [dispatch, imageData.imageId]
  );

  const debouncedCornersUpdate = useCallback(
    debounce((corners) => {
      dispatch(canopyActions.updateImportedImages("edit_image", { corners }, imageData.imageId));
    }, 500),
    [dispatch, imageData.imageId]
  );

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!map || !imageData) return;

    const imageId = `imported-image-${imageData.imageId}`;
    const polygonId = `image-polygon-${imageData.imageId}`;

    // Don't reinitialize if already exists - just update opacity and visibility
    if (isInitializedRef.current && map.getSource(imageId)) {
      if (map.getLayer(imageId)) {
        map.setPaintProperty(imageId, "raster-opacity", opacity);
      }

      // Ensure markers and outline visibility match editMode
      if (markersRef.current.length > 0) {
        markersRef.current.forEach((marker) => {
          if (marker && marker.getElement()) {
            marker.getElement().style.display = editMode.current ? "block" : "none";
          }
        });
      }

      if (map.getLayer(`${polygonId}-outline`)) {
        map.setPaintProperty(`${polygonId}-outline`, "line-opacity", editMode.current ? 0.5 : 0);
      }

      return;
    }

    // Mark as initialized
    isInitializedRef.current = true;
    // console.log("imageData:", imageData);

    const imageBlob = new Image();
    imageBlob.src = imageData.imgSrc;

    imageBlob.onload = () => {
      const center = map.getCenter();
      const aspectRatio = imageBlob.width / imageBlob.height;
      const imageHeight = 0.005;
      const imageWidth = imageHeight * aspectRatio;

      const initialCoordinates = imageData.corners
        ? imageData.corners
        : [
            [center.lng - imageWidth / 2, center.lat + imageHeight / 2],
            [center.lng + imageWidth / 2, center.lat + imageHeight / 2],
            [center.lng + imageWidth / 2, center.lat - imageHeight / 2],
            [center.lng - imageWidth / 2, center.lat - imageHeight / 2],
          ];

      if (!imageData.corners) {
        // Save corners immediately without debounce for initial setup
        dispatch(canopyActions.updateImportedImages("edit_image", { corners: initialCoordinates }, imageData.imageId));
      }

      const polyCoords = [...initialCoordinates, initialCoordinates[0]];

      updatedCoordinatesRef.current = initialCoordinates;
      const midPoint = getMidPoint(initialCoordinates[0], initialCoordinates[1]);
      setPopupPosition(midPoint.geometry.coordinates);

      const targetLayerId = map.getStyle().layers[4].id;

      map.addSource(imageId, {
        type: "image",
        url: imageBlob.src,
        coordinates: initialCoordinates,
      });

      map.addLayer(
        {
          id: imageId,
          type: "raster",
          source: imageId,
          paint: { "raster-opacity": opacityRef.current },
        },
        targetLayerId
      );

      map.addSource(polygonId, {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [polyCoords] },
        },
      });

      map.addLayer(
        {
          id: polygonId,
          type: "fill",
          source: polygonId,
          paint: {
            "fill-color": "transparent",
            "fill-opacity": 0,
          },
        },
        targetLayerId
      );

      // Add outline layer - only visible in edit mode
      map.addLayer(
        {
          id: `${polygonId}-outline`,
          type: "line",
          source: polygonId,
          paint: {
            "line-color": "#002bcb",
            "line-width": 2,
            "line-opacity": editMode.current ? 0.5 : 0,
          },
        },
        targetLayerId
      );

      const createMarker = (coord, index) => {
        const el = document.createElement("div");
        el.className = "image-marker";
        el.style.width = "15px";
        el.style.height = "15px";
        el.style.backgroundColor = "#002bcb";
        el.style.border = "2px solid #fff";
        el.style.borderRadius = "50%";
        el.style.display = editMode.current ? "block" : "none";

        const marker = new mapboxgl.Marker(el).setLngLat(coord).addTo(map);

        el.addEventListener("mousedown", (e) => {
          e.stopPropagation();
          if (editMode.current) {
            currentMarkerRef.current = index;
            map.getCanvas().style.cursor = "nwse-resize";
          }
        });

        return marker;
      };

      markersRef.current = initialCoordinates.map((coord, index) => createMarker(coord, index));

      // Double-click handler with better targeting
      const handleDoubleClick = (e) => {
        if (is_drawing) return;

        // Query the image layer first (always visible)
        const imageFeatures = map.queryRenderedFeatures(e.point, {
          layers: [imageId],
        });

        // Also check polygon layers in case clicking near edge
        const polygonFeatures = map.queryRenderedFeatures(e.point, {
          layers: [polygonId, `${polygonId}-outline`],
        });

        // If no features found on any of our layers, ignore
        if (imageFeatures.length === 0 && polygonFeatures.length === 0) return;

        // Stop the event from propagating to other handlers
        e.preventDefault();
        if (e.originalEvent) {
          e.originalEvent.stopPropagation();
          e.originalEvent.stopImmediatePropagation();
        }

        // Toggle edit mode
        const newEditMode = !editMode.current;
        editMode.current = newEditMode;
        setPopupVisible(newEditMode);

        // Update marker visibility
        markersRef.current.forEach((marker) => {
          if (marker && marker.getElement()) {
            marker.getElement().style.display = newEditMode ? "block" : "none";
          }
        });

        // Update outline visibility
        if (map.getLayer(`${polygonId}-outline`)) {
          map.setPaintProperty(`${polygonId}-outline`, "line-opacity", newEditMode ? 0.5 : 0);
        }

        if (newEditMode) {
          set_selected_image(imageData.imageId);
        } else {
          set_selected_image(undefined);
        }
      };

      // Single click handler to prevent HandleFeatureClick interference when editing
      const handleClick = (e) => {
        // Only block clicks when in edit mode
        if (!editMode.current) return;

        // Check if clicking on this image
        const imageFeatures = map.queryRenderedFeatures(e.point, {
          layers: [imageId],
        });

        const polygonFeatures = map.queryRenderedFeatures(e.point, {
          layers: [polygonId, `${polygonId}-outline`],
        });

        // If clicking on our image, stop propagation to prevent HandleFeatureClick from interfering
        if (imageFeatures.length > 0 || polygonFeatures.length > 0) {
          if (e.originalEvent) {
            e.originalEvent.stopPropagation();
            e.originalEvent.stopImmediatePropagation();
          }
        }
      };

      // Store event handlers for cleanup
      eventHandlersRef.current.handleDoubleClick = handleDoubleClick;
      eventHandlersRef.current.handleClick = handleClick;

      map.on("click", handleClick);
      map.on("dblclick", handleDoubleClick);

      const handleMouseDown = (e) => {
        if (!editMode.current) return;
        if (markersRef.current.length === 0) return; // Don't allow interaction until fully initialized

        if (map.getLayer(polygonId)) {
          const features = map.queryRenderedFeatures(e.point, { layers: [polygonId] });

          if (features.length) {
            e.preventDefault();
            if (e.originalEvent) e.originalEvent.stopPropagation();
            draggingRef.current = true;
            startCoordRef.current = e.lngLat;
            map.getCanvas().style.cursor = "move";
            map.dragPan.disable();
          }
        }
      };

      const handleMouseMove = (e) => {
        if (!editMode.current) return;
        if (markersRef.current.length === 0) return; // Don't allow interaction until fully initialized

        if (draggingRef.current || currentMarkerRef.current !== null) {
          e.preventDefault();
          if (e.originalEvent) e.originalEvent.stopPropagation();
        }

        if (draggingRef.current) {
          const dx = e.lngLat.lng - startCoordRef.current.lng;
          const dy = e.lngLat.lat - startCoordRef.current.lat;

          const movedCoordinates = updatedCoordinatesRef.current.map(([lng, lat]) => [lng + dx, lat + dy]);
          const newMidPoint = getMidPoint(movedCoordinates[0], movedCoordinates[1]);
          setPopupPosition(newMidPoint.geometry.coordinates);

          // Use requestAnimationFrame for smoother updates
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }

          animationFrameRef.current = requestAnimationFrame(() => {
            try {
              const polySource = map.getSource(polygonId);
              if (polySource) {
                polySource.setData({
                  type: "Feature",
                  geometry: { type: "Polygon", coordinates: [[...movedCoordinates, movedCoordinates[0]]] },
                });
              }

              const imgSource = map.getSource(imageId);
              if (imgSource) {
                imgSource.setCoordinates(movedCoordinates);
              }

              // Ensure opacity stays consistent during drag
              if (map.getLayer(imageId)) {
                map.setPaintProperty(imageId, "raster-opacity", opacityRef.current);
              }
            } catch (err) {
              console.warn("Error updating coordinates:", err);
            }
          });

          markersRef.current.forEach((marker, index) => marker.setLngLat(movedCoordinates[index]));
          updatedCoordinatesRef.current = movedCoordinates;
          startCoordRef.current = e.lngLat;
        }

        if (editMode.current && currentMarkerRef.current !== null) {
          const updatedCoordinates = [...updatedCoordinatesRef.current];
          updatedCoordinates[currentMarkerRef.current] = [e.lngLat.lng, e.lngLat.lat];

          const newMidPoint = getMidPoint(updatedCoordinates[0], updatedCoordinates[1]);
          setPopupPosition(newMidPoint.geometry.coordinates);

          // Use requestAnimationFrame for smoother updates
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }

          animationFrameRef.current = requestAnimationFrame(() => {
            try {
              const polySource = map.getSource(polygonId);
              if (polySource) {
                polySource.setData({
                  type: "Feature",
                  geometry: { type: "Polygon", coordinates: [[...updatedCoordinates, updatedCoordinates[0]]] },
                });
              }

              const imgSource = map.getSource(imageId);
              if (imgSource) {
                imgSource.setCoordinates(updatedCoordinates);
              }

              // Ensure opacity stays consistent during drag
              if (map.getLayer(imageId)) {
                map.setPaintProperty(imageId, "raster-opacity", opacityRef.current);
              }
            } catch (err) {
              console.warn("Error updating coordinates:", err);
            }
          });

          markersRef.current.forEach((marker, index) => marker.setLngLat(updatedCoordinates[index]));
          updatedCoordinatesRef.current = updatedCoordinates;
          debouncedCornersUpdate(updatedCoordinatesRef.current);
        }
      };

      const handleMouseUp = () => {
        // Always re-enable dragPan if it was disabled, regardless of edit mode
        if (draggingRef.current) {
          draggingRef.current = false;
          map.getCanvas().style.cursor = "";
          map.dragPan.enable();
          // Save final position to redux only if still in edit mode
          if (editMode.current) {
            debouncedCornersUpdate(updatedCoordinatesRef.current);
          }
        }
        if (currentMarkerRef.current !== null) {
          currentMarkerRef.current = null;
          map.getCanvas().style.cursor = "";
        }
      };

      // Store event handlers for cleanup
      eventHandlersRef.current.handleDoubleClick = handleDoubleClick;
      eventHandlersRef.current.handleClick = handleClick;
      eventHandlersRef.current.handleMouseDown = handleMouseDown;
      eventHandlersRef.current.handleMouseMove = handleMouseMove;
      eventHandlersRef.current.handleMouseUp = handleMouseUp;

      map.on("click", handleClick);
      map.on("dblclick", handleDoubleClick);
      map.on("mousedown", handleMouseDown);
      map.on("mousemove", handleMouseMove);
      map.on("mouseup", handleMouseUp);
    };

    return () => {
      // Cleanup event listeners
      if (eventHandlersRef.current.handleClick) {
        map.off("click", eventHandlersRef.current.handleClick);
      }
      if (eventHandlersRef.current.handleDoubleClick) {
        map.off("dblclick", eventHandlersRef.current.handleDoubleClick);
      }
      if (eventHandlersRef.current.handleMouseDown) {
        map.off("mousedown", eventHandlersRef.current.handleMouseDown);
      }
      if (eventHandlersRef.current.handleMouseMove) {
        map.off("mousemove", eventHandlersRef.current.handleMouseMove);
      }
      if (eventHandlersRef.current.handleMouseUp) {
        map.off("mouseup", eventHandlersRef.current.handleMouseUp);
      }

      // Cancel any pending animation frames
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Reset initialization flag so component can reinitialize if remounted
      isInitializedRef.current = false;
    };
  }, [map, imageData.imageId, debouncedCornersUpdate]);

  // Separate effect to handle opacity changes without re-initializing
  useEffect(() => {
    if (!map || !imageData) return;
    const imageId = `imported-image-${imageData.imageId}`;

    if (map.getLayer(imageId)) {
      map.setPaintProperty(imageId, "raster-opacity", opacity);
    }
  }, [map, imageData.imageId, opacity]);

  const handleOpacityChange = (e) => {
    const newOpacity = parseFloat(e.target.value);

    setOpacity(newOpacity);
    if (map.getLayer(`imported-image-${imageData.imageId}`)) {
      map.setPaintProperty(`imported-image-${imageData.imageId}`, "raster-opacity", newOpacity);
    }
    debouncedOpacityUpdate(newOpacity);
  };

  const toggleEditMode = () => {
    if (draggingRef.current) return;

    const imageId = `imported-image-${imageData.imageId}`;
    const polygonId = `image-polygon-${imageData.imageId}`;

    editMode.current = !editMode.current;
    setPopupVisible(editMode.current);

    markersRef.current.forEach((marker) => {
      marker.getElement().style.display = editMode.current ? "block" : "none";
    });

    // Update outline visibility
    if (map.getLayer(`${polygonId}-outline`)) {
      map.setPaintProperty(`${polygonId}-outline`, "line-opacity", editMode.current ? 0.5 : 0);
    }
  };

  const deleteImage = () => {
    dispatch(canopyActions.updateImportedImages("delete_image", imageData.imageId));

    const imageId = `imported-image-${imageData.imageId}`;
    const polygonId = `image-polygon-${imageData.imageId}`;

    // Remove layers
    if (map.getLayer(imageId)) {
      map.removeLayer(imageId);
    }
    if (map.getLayer(polygonId)) {
      map.removeLayer(polygonId);
    }
    if (map.getLayer(`${polygonId}-outline`)) {
      map.removeLayer(`${polygonId}-outline`);
    }

    // Remove sources
    if (map.getSource(imageId)) {
      map.removeSource(imageId);
    }
    if (map.getSource(polygonId)) {
      map.removeSource(polygonId);
    }

    // Remove markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Cancel any pending animation frames
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  return (
    <>
      {editMode.current && popupPosition && popupVisible && (
        <Marker latitude={popupPosition[1] + 0.0003} longitude={popupPosition[0]} anchor="bottom">
          <img src={invisibleMarker} alt="Invisible Marker" />
          <Popup latitude={popupPosition[1] + 0.0003} longitude={popupPosition[0]} closeButton={true} onClose={toggleEditMode}>
            <section style={{ display: "flex", marginBottom: "8px" }}>
              <Popconfirm title="Double-click the image to re-enter edit mode" onConfirm={toggleEditMode} okText="Okay" showCancel={false}>
                <Button size="small">
                  <img style={{ width: "14px" }} src={lock} />
                </Button>
              </Popconfirm>
              <Popconfirm title="Delete image?" onConfirm={deleteImage} okText="Yes" cancelText="No">
                <Button size="small">
                  <img style={{ width: "14px" }} src={delete_single_polygon} />
                </Button>
              </Popconfirm>
            </section>
            <section style={{ display: "flex", alignItems: "center", marginBottom: 5 }}>
              <label htmlFor="opacity">Opacity: </label>
              <input type="range" id="opacity" min="0" max="1" step="0.1" value={opacity} onChange={handleOpacityChange} />
              <span>{Math.round(opacity * 100)}%</span>
            </section>
            <span style={{ whiteSpace: "nowrap" }}>*Double-click Image to enable edit mode*</span>
          </Popup>
        </Marker>
      )}
    </>
  );
};

export { MapboxImageLayerContainer, MapboxImageLayer };
